<template>
  <div class="mixer-game" :class="{ 'game-active': gameStarted }">
    <template v-if="!showLevelComplete">
      <div class="instructions">
        <h2 class="question">
          Drag the {{ currentTarget.emoji }} to the mixer and blend!
        </h2>
        <div class="fruit-selection">
          <div
            v-for="fruit in fruits"
            :key="fruit.name"
            class="fruit-item"
            draggable="true"
            @dragstart="onDragStart($event, fruit)"
            @touchstart="onTouchStart($event, fruit)"
            @touchmove="onTouchMove"
            @touchend="onTouchEnd"
          >
            {{ fruit.emoji }}
            <div class="fruit-label">{{ fruit.name }}</div>
          </div>
        </div>
      </div>

      <div class="mixer-container">
        <div
          ref="dropZone"
          class="drop-zone"
          :class="{
            'drag-over': isDragOver,
            'has-orange': hasOrange,
            'incorrect-fruit': showIncorrectFeedback
          }"
          @dragover="onDragOver"
          @dragleave="onDragLeave"
          @drop="onDrop"
        >
          <div class="aspect-wrapper">
            <canvas
              ref="canvas"
              class="rive-canvas"
              @click="onCanvasClick"
              tabindex="0"
            ></canvas>
          </div>

          <div v-if="!hasOrange && !showIncorrectFeedback" class="drop-hint">
            Drop {{ currentTarget.emoji }} here
          </div>
          <div v-if="hasOrange && !hasBlended" class="success-hint">
            {{ currentTarget.emoji }} Click to blend!
          </div>
          <div v-if="showIncorrectFeedback" class="error-hint">
            Drop the {{ currentTarget.name }}.
          </div>
        </div>
      </div>

      <div
        v-if="dragPreview.show"
        class="drag-preview"
        :style="{ left: dragPreview.x + 'px', top: dragPreview.y + 'px' }"
      >
        {{ dragPreview.emoji }}
      </div>
    </template>

    <LevelComplete v-else />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, nextTick } from "vue";
import { Rive, Layout, Fit, Alignment } from "rive-js";
import LevelComplete from "../LevelComplete.vue";
import { useSpeechSynthesis } from "@/composables/useSpeechSynthesis";

/* Refs */
const canvas = ref(null);
const dropZone = ref(null);

/* Rive */
const riveUrl = "/mixer.riv";

/* Speech */
const { speakOutLoud } = useSpeechSynthesis();

/* Game state */
const gameStarted = ref(false);
const hasOrange = ref(false); // generic "correct fruit in drop zone" (kept name for CSS)
const isDragOver = ref(false);
const showIncorrectFeedback = ref(false);
const hasBlended = ref(false);
const showLevelComplete = ref(false);

/* Fruit sequence (order matters) */
const fruits = [
  { emoji: "🍊", name: "Orange", input: "Orange" },
  { emoji: "🍇", name: "Grape", input: "Grape" },
  { emoji: "🍌", name: "Banana", input: "Banana" },
  { emoji: "🍎", name: "Apple", input: "Apple" }
];

const currentStage = ref(0);
const currentTarget = computed(() => fruits[currentStage.value]);

/* Drag preview (touch helper) */
const dragPreview = ref({ show: false, emoji: "", x: 0, y: 0 });
let currentDragFruit = null;

/* Rive runtime handles */
let riveInstance = null;
let isUnmounted = false;
let blendStarted = false;
let currentAnimName = "Off";

/* Timers */
let stageCompletionTimer = null;
let offResetTimer = null;

/* Resize + DPR handling */
let resizeRaf = 0;
let resizeObserver = null;
let dpr = Math.max(1, window.devicePixelRatio || 1);
const DPR_CAP = 3; // cap for performance while keeping crisp visuals

/* Animations */
const Anim = {
  Off: "Off",
  Orange: "Orange",
  Grape: "Grape",
  Banana: "Banana",
  Apple: "Apple"
};

/* --- Rive helpers --- */
function safeCleanupRive() {
  if (!riveInstance) return;
  try {
    if (typeof riveInstance.stopRendering === "function") {
      riveInstance.stopRendering();
    }
  } catch (e) {
    console.warn("Rive stopRendering failed:", e);
  }
  try {
    riveInstance.cleanup();
  } catch (e) {
    console.warn("Rive cleanup failed:", e);
  }
  riveInstance = null;
}

/* Backing-store resize for crisp rendering.
   - CSS keeps canvas 100% of parent.
   - We only adjust canvas width/height attributes to CSS pixels * DPR.
   - Then ask Rive to resize to canvas. */
function updateCanvasBackingStore() {
  if (!canvas.value) return;
  const parent = canvas.value.parentElement;
  if (!parent) return;

  const rect = parent.getBoundingClientRect();
  if (rect.width === 0 || rect.height === 0) return;

  const targetDpr = Math.min(Math.max(1, window.devicePixelRatio || 1), DPR_CAP);
  const pxW = Math.floor(rect.width * targetDpr);
  const pxH = Math.floor(rect.height * targetDpr);

  // Only touch when changed to avoid extra work
  const c = canvas.value;
  let changed = false;
  if (c.width !== pxW) {
    c.width = pxW;
    changed = true;
  }
  if (c.height !== pxH) {
    c.height = pxH;
    changed = true;
  }

  // If DPR changed or backing store changed, notify Rive
  const dprChanged = targetDpr !== dpr;
  dpr = targetDpr;

  if (riveInstance && (changed || dprChanged)) {
    try {
      riveInstance.resizeDrawingSurfaceToCanvas();
    } catch (e) {
      console.warn("Rive resizeDrawingSurfaceToCanvas failed:", e);
    }
  }
}

function requestCanvasResize() {
  if (resizeRaf) cancelAnimationFrame(resizeRaf);
  resizeRaf = requestAnimationFrame(() => {
    if (!isUnmounted) updateCanvasBackingStore();
  });
}

function playOff() {
  if (!riveInstance) return;
  if (currentAnimName === Anim.Off) return;
  try {
    riveInstance.play(Anim.Off);
    currentAnimName = Anim.Off;
  } catch (e) {
    console.warn("Rive play Off failed:", e);
  }
}

function playFruitAnimation(name) {
  if (!riveInstance) return;
  const valid = [Anim.Orange, Anim.Grape, Anim.Banana, Anim.Apple];
  const animName = valid.includes(name) ? name : Anim.Off;

  if (currentAnimName === animName) return;

  try {
    riveInstance.play(animName);
    currentAnimName = animName;
  } catch (e) {
    console.warn(`Rive play ${animName} failed:`, e);
  }

  clearTimeout(offResetTimer);
  offResetTimer = setTimeout(() => {
    if (!isUnmounted) playOff();
  }, 4000);
}

async function initRiveOnce() {
  if (!canvas.value || isUnmounted) return;

  // Ensure backing store is correct before creating the instance
  updateCanvasBackingStore();

  riveInstance = new Rive({
    src: riveUrl,
    canvas: canvas.value,
    autoplay: true,
    animations: Anim.Off,
    layout: new Layout({ fit: Fit.Contain, alignment: Alignment.Center }),
    onLoad: () => {
      if (isUnmounted) return;
      playOff();
      // One more resize after load to ensure GL surface is perfect
      requestCanvasResize();
    },
    onLoadError: (err) => {
      console.error("Failed to load Rive file:", err);
    }
  });
}

/* Progression */
function proceedToLevelComplete() {
  if (isUnmounted) return;
  showLevelComplete.value = true;
  setTimeout(() => {
    window.location.href = "/professions";
  }, 4000);
}

function proceedToNextFruit() {
  hasOrange.value = false;
  hasBlended.value = false;
  blendStarted = false;
  playOff();

  if (currentStage.value < fruits.length - 1) {
    currentStage.value += 1;
  } else {
    proceedToLevelComplete();
  }
}

function speakRandomPraise() {
  const phrases = ["Well done!", "Great job!", "Nice!", "Awesome!"];
  const randomPhrase = phrases[Math.floor(Math.random() * phrases.length)];
  speakOutLoud(randomPhrase);
}

/* Click to blend */
function onCanvasClick() {
  if (!riveInstance) return;
  if (!hasOrange.value || hasBlended.value || blendStarted || isUnmounted) return;

  hasBlended.value = true;
  blendStarted = true;

  playFruitAnimation(currentTarget.value.input);

  playSound("https://commondatastorage.googleapis.com/codeskulptor-assets/week7-brrring.m4a");

// Example call
  speakRandomPraise();


  clearTimeout(stageCompletionTimer);
  stageCompletionTimer = setTimeout(() => {
    if (!isUnmounted) proceedToNextFruit();
  }, 4000);
}

/* DnD + touch */
const onDragStart = (event, fruit) => {
  currentDragFruit = fruit;
  if (event?.dataTransfer) {
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("text/plain", fruit.emoji);
  }
  speakOutLoud(fruit.name);
};
const onDragOver = (event) => {
  event.preventDefault();
  isDragOver.value = true;
};
const onDragLeave = () => {
  isDragOver.value = false;
};
const onDrop = (event) => {
  event.preventDefault();
  isDragOver.value = false;
  handleFruitDrop(currentDragFruit);
};

const onTouchStart = (event, fruit) => {
  event.preventDefault();
  currentDragFruit = fruit;
  speakOutLoud(fruit.name);
  const touch = event.touches[0];
  dragPreview.value = {
    show: true,
    emoji: fruit.emoji,
    x: touch.clientX - 25,
    y: touch.clientY - 25
  };
};
const onTouchMove = (event) => {
  event.preventDefault();
  if (dragPreview.value.show) {
    const touch = event.touches[0];
    dragPreview.value.x = touch.clientX - 25;
    dragPreview.value.y = touch.clientY - 25;
  }
};
const onTouchEnd = (event) => {
  event.preventDefault();
  if (dragPreview.value.show && dropZone.value) {
    const touch = event.changedTouches[0];
    const rect = dropZone.value.getBoundingClientRect();
    const inside =
      touch.clientX >= rect.left &&
      touch.clientX <= rect.right &&
      touch.clientY >= rect.top &&
      touch.clientY <= rect.bottom;

    if (inside) handleFruitDrop(currentDragFruit);
  }
  dragPreview.value.show = false;
  currentDragFruit = null;
};

function handleFruitDrop(fruit) {
  if (!fruit || isUnmounted) return;
  showIncorrectFeedback.value = false;

  const isCorrect = fruit.name === currentTarget.value.name;
  if (isCorrect) {
    hasOrange.value = true; // keep class contract
    gameStarted.value = true;
    playSound("https://commondatastorage.googleapis.com/codeskulptor-assets/week7-brrring.m4a");
  } else {
    showIncorrectFeedback.value = true;
    playSound("https://commondatastorage.googleapis.com/codeskulptor-demos/duck.ogg");
    setTimeout(() => {
      if (!isUnmounted) showIncorrectFeedback.value = false;
    }, 1600);
  }
}

/* Audio helper */
function playSound(url) {
  try {
    const a = new Audio(url);
    const p = a.play();
    if (p && typeof p.catch === "function") {
      p.catch((err) => {
        console.debug("Audio play blocked or failed:", err?.message || err);
      });
    }
  } catch (e) {
    console.warn("Audio failed:", e);
  }
}

/* Resize handling — strictly resize backing store, never recreate Rive */
function handleResize() {
  requestCanvasResize();
}

let resizeListener = null;
let orientationListener = null;

onMounted(async () => {
  isUnmounted = false;
  await nextTick();

  // Observe the canvas parent for precise size changes (layout, container changes, etc.)
  if (canvas.value?.parentElement) {
    resizeObserver = new ResizeObserver(() => requestCanvasResize());
    resizeObserver.observe(canvas.value.parentElement);
  }

  // Initial sizing before Rive init
  updateCanvasBackingStore();
  await initRiveOnce();

  // Also listen to window resizes/orientation (covers DPR changes/zoom)
  resizeListener = handleResize;
  orientationListener = () => setTimeout(handleResize, 150);

  window.addEventListener("resize", resizeListener, { passive: true });
  window.addEventListener("orientationchange", orientationListener, { passive: true });

  // Kick one more resize in RAF
  requestCanvasResize();
});

onBeforeUnmount(() => {
  isUnmounted = true;

  if (resizeRaf) cancelAnimationFrame(resizeRaf);
  clearTimeout(stageCompletionTimer);
  clearTimeout(offResetTimer);

  if (resizeObserver && canvas.value?.parentElement) {
    try {
      resizeObserver.unobserve(canvas.value.parentElement);
    } catch (e) {console.log(e)}
  }
  resizeObserver = null;

  if (resizeListener) window.removeEventListener("resize", resizeListener);
  if (orientationListener) window.removeEventListener("orientationchange", orientationListener);

  safeCleanupRive();
});
</script>

<style scoped>
.mixer-game {
  padding: 16px;
  max-width: 480px;
  margin: 0 auto;
  text-align: center;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}
.question {
  font-size: 1.5rem;
  margin-bottom: 16px;
  background: #fff;
  padding: 10px;
  border-radius: 12px;
  color: #333;
}
.instructions {
  margin-bottom: 20px;
}
.fruit-selection {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  margin-bottom: 10px;
}
.fruit-item {
  background: #fff;
  border-radius: 12px;
  padding: 16px;
  font-size: 2rem;
  cursor: grab;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s, box-shadow 0.2s;
  user-select: none;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}
.fruit-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}
.fruit-item:active {
  cursor: grabbing;
  transform: scale(0.95);
}
.fruit-label {
  font-size: 0.8rem;
  color: #666;
  font-weight: 500;
}
.mixer-container {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  transition: all 0.5s ease-in-out;
}
.drop-zone {
  position: relative;
  width: 100%;
  max-width: 350px;
  aspect-ratio: 1 / 1;
  border: 3px dashed #ddd;
  border-radius: 20px;
  transition: all 0.3s ease;
  background: rgba(255, 255, 255, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}
.aspect-wrapper {
  width: 100%;
  height: 100%;
  aspect-ratio: 1 / 1;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
}
.rive-canvas {
  /* Fill the parent; backing store is sized via JS to DPR for crispness */
  width: 100% !important;
  height: 100% !important;
  display: block;
  max-width: 100%;
  max-height: 100%;
  outline: none;
  background: transparent;
  cursor: pointer;
}
.drop-zone.drag-over {
  border-color: #4caf50;
  background: rgba(76, 175, 80, 0.1);
  transform: scale(1.02);
}
.drop-zone.has-orange {
  border-color: #ff9800;
  background: rgba(255, 152, 0, 0.1);
}
.drop-zone.incorrect-fruit {
  border-color: #f44336;
  background: rgba(244, 67, 54, 0.1);
  animation: shake 0.5s ease-in-out;
}
.drop-hint,
.success-hint,
.error-hint {
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(255, 255, 255, 0.9);
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 500;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  pointer-events: none;
}
.success-hint {
  background: rgba(76, 175, 80, 0.9);
  color: white;
}
.error-hint {
  background: rgba(244, 67, 54, 0.9);
  color: white;
}
.drag-preview {
  position: fixed;
  font-size: 2rem;
  pointer-events: none;
  z-index: 1000;
  transform: scale(1.2);
  opacity: 0.8;
}
.mixer-game.game-active .mixer-container {
  align-items: flex-start;
  padding-top: 20px;
}
@keyframes shake {
  0%,
  100% {
    transform: translateX(0);
  }
  25% {
    transform: translateX(-5px);
  }
  75% {
    transform: translateX(5px);
  }
}
@media (max-width: 480px) {
  .mixer-game {
    padding: 8px;
  }
  .question {
    font-size: 1.1rem;
    padding: 8px;
  }
  .fruit-item {
    padding: 10px;
    font-size: 1.4rem;
  }
  .fruit-label {
    font-size: 0.7rem;
  }
  .drop-zone {
    max-width: 98vw;
    min-width: 140px;
  }
  .drop-hint,
  .success-hint,
  .error-hint {
    font-size: 0.8rem;
    bottom: 10px;
  }
  .mixer-game.game-active .mixer-container {
    padding-top: 10px;
  }
}
@media (max-height: 600px) {
  .mixer-container {
    min-height: 180px;
  }
  .drop-zone {
    max-width: 82vw;
    min-width: 110px;
  }
}
</style>